'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ServiceCardProps {
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  priceRange: string;
  features: string[];
  category: string;
}

export default function ServiceCard({
  title,
  description,
  icon,
  image,
  imageAlt,
  priceRange,
  features,
  category
}: ServiceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border">
        <div className="relative h-48 bg-muted"></div>
        <div className="p-6">
          <div className="h-6 bg-muted rounded mb-2"></div>
          <div className="h-4 bg-muted rounded mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border hover:shadow-lg transition-shadow duration-300">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={image}
          alt={imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
          {category}
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 w-10 h-10 bg-surface rounded-lg flex items-center justify-center">
            <Icon name={icon as any} size={24} className="text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-heading font-bold text-foreground mb-1">
              {title}
            </h3>
            <p className="text-sm text-accent font-semibold">{priceRange}</p>
          </div>
        </div>

        <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
          {description}
        </p>

        <div className="space-y-2 mb-4">
          {features.slice(0, isExpanded ? features.length : 3).map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Icon
                name="CheckCircleIcon"
                size={16}
                variant="solid"
                className="text-success mt-0.5 flex-shrink-0"
              />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>

        {features.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-primary hover:text-secondary font-medium flex items-center gap-1 mb-4 transition-colors duration-300"
          >
            {isExpanded ? 'Voir moins' : 'Voir plus'}
            <Icon
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={16}
              className="text-primary"
            />
          </button>
        )}

        <Link 
          href={`/quote-request?service=${encodeURIComponent(title)}`}
          className="w-full bg-primary hover:bg-secondary text-primary-foreground font-medium py-3 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
        >
          <Icon name="DocumentTextIcon" size={20} />
          Demander un devis
        </Link>
      </div>
    </div>
  );
}